import type { Theme, Components, ComponentsVariants } from "@mui/material/styles"

import { avatarGroupClasses } from "@mui/material/AvatarGroup"

import { varAlpha } from "../../styles"

// ----------------------------------------------------------------------

// NEW VARIANT
declare module "@mui/material/AvatarGroup" {
    interface AvatarGroupPropsVariantOverrides {
        compact: true
    }
}

const COLORS = ["primary", "secondary", "info", "success", "warning", "error"] as const

export const colorByName = (name: string): { color: string; bgColor: string } => {
    // Handle undefined, null, or empty string
    if (!name || typeof name !== "string") {
        return { color: "#2A1F2D", bgColor: "#EDEBEE" }
    }

    const charAt = name.toLowerCase().charCodeAt(0)

    if (charAt >= 97 && charAt <= 100) return { color: "#BD5B00", bgColor: "#FFF9DB" }
    if (charAt >= 101 && charAt <= 105) return { color: "#004BA9", bgColor: "#EBF3FE" }
    if (charAt >= 106 && charAt <= 110) return { color: "#512194", bgColor: "#F1EAFA" }
    if (charAt >= 111 && charAt <= 115) return { color: "#005C62", bgColor: "#E7F9F9" }
    if (charAt >= 116 && charAt <= 120) return { color: "#A42700", bgColor: "#FFF2EB" }
    if (charAt >= 121 && charAt <= 122) return { color: "#2A1F2D", bgColor: "#EDEBEE" }
    return { color: "#2A1F2D", bgColor: "#EDEBEE" }
}

// ----------------------------------------------------------------------

const avatarColors: Record<string, ComponentsVariants<Theme>["MuiAvatar"]> = {
    colors: COLORS.map((color) => ({
        props: ({ ownerState }) => ownerState.color === color,
        style: ({ theme }) => ({
            color: theme.vars.palette[color].contrastText,
            backgroundColor: theme.vars.palette[color].main,
        }),
    })),
    defaultColor: [
        {
            props: ({ ownerState }) => ownerState.color === "default",
            style: ({ theme }) => ({
                color: theme.vars.palette.text.secondary,
                backgroundColor: varAlpha(theme.vars.palette.grey["500Channel"], 0.24),
            }),
        },
    ],
}

const MuiAvatar: Components<Theme>["MuiAvatar"] = {
    /** **************************************
     * VARIANTS
     *************************************** */
    variants: [...[...avatarColors.defaultColor!, ...avatarColors.colors!]],

    /** **************************************
     * STYLE
     *************************************** */
    styleOverrides: {
        rounded: ({ theme }) => ({ borderRadius: theme.shape.borderRadius * 1.5 }),
        colorDefault: ({ ownerState, theme }) => {
            const color = colorByName(`${ownerState.alt}`)

            return {
                ...(!!ownerState.alt && {
                    color: color.color,
                    backgroundColor: color.bgColor,
                }),
            }
        },
    },
}

// ----------------------------------------------------------------------

const MuiAvatarGroup: Components<Theme>["MuiAvatarGroup"] = {
    /** **************************************
     * DEFAULT PROPS
     *************************************** */
    defaultProps: { max: 4 },

    /** **************************************
     * STYLE
     *************************************** */
    styleOverrides: {
        root: ({ ownerState }) => ({
            justifyContent: "flex-end",
            ...(ownerState.variant === "compact" && {
                width: 40,
                height: 40,
                position: "relative",
                [`& .${avatarGroupClasses.avatar}`]: {
                    margin: 0,
                    width: 28,
                    height: 28,
                    position: "absolute",
                    "&:first-of-type": { left: 0, bottom: 0, zIndex: 9 },
                    "&:last-of-type": { top: 0, right: 0 },
                },
            }),
        }),
        avatar: ({ theme }) => ({
            fontSize: 16,
            fontWeight: theme.typography.fontWeightSemiBold,
            "&:first-of-type": {
                fontSize: 12,
                color: theme.vars.palette.primary.dark,
                backgroundColor: theme.vars.palette.primary.lighter,
            },
        }),
    },
}

// ----------------------------------------------------------------------

export const avatar = { MuiAvatar, MuiAvatarGroup }
